import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiLock, FiArrowRight, FiShield, FiKey, FiEye,
  FiAlertTriangle, FiCheckCircle
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const AdminSecurity = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/admin/security" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/admin" className="hover:text-blue-600">Admin & Permissions</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Security Settings</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-orange-100 dark:bg-orange-900/30 rounded-xl mb-4">
              <FiLock className="w-6 h-6 text-orange-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Security Settings</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Configure security policies, authentication methods, and access controls for your organization.
            </p>
          </div>

          {/* Security Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Security at Tasklina</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                We take security seriously. Tasklina provides multiple layers of security 
                to protect your organization's data and ensure compliance with industry standards.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <FiShield className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm">Data Encryption</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">AES-256 at rest, TLS in transit</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <FiKey className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm">2FA Support</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">TOTP authenticator apps</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <FiEye className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm">Audit Logs</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Complete activity tracking</p>
                </div>
              </div>
            </div>
          </section>

          {/* Two-Factor Authentication */}
          <section className="mb-16" id="2fa">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Two-Factor Authentication (2FA)</h2>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Add an extra layer of security by requiring a second form of verification when signing in.
              </p>
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">Enabling 2FA for Your Organization</h4>
              <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-2 list-decimal list-inside mb-4">
                <li>Go to Organization Settings → Security</li>
                <li>Toggle "Require Two-Factor Authentication"</li>
                <li>Choose grace period for users to set up 2FA</li>
                <li>Users will be prompted to enable 2FA on next login</li>
              </ol>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <FiAlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    Requiring 2FA organization-wide may lock out users who haven't set it up. 
                    Use the grace period to give users time to comply.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Password Policy */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Password Policy</h2>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <FiCheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-gray-700 dark:text-gray-200">Minimum 8 characters required</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <FiCheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-gray-700 dark:text-gray-200">Mix of uppercase and lowercase letters</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <FiCheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-gray-700 dark:text-gray-200">At least one number required</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <FiCheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-gray-700 dark:text-gray-200">Special character recommended</span>
              </div>
            </div>
          </section>

          {/* Session Management */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Session Management</h2>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Control how long user sessions remain active and manage active sessions.
              </p>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li>• <strong>Session timeout:</strong> Configure automatic logout after inactivity</li>
                <li>• <strong>Active sessions:</strong> View and revoke user sessions</li>
                <li>• <strong>Remember me:</strong> Allow extended sessions on trusted devices</li>
              </ul>
            </div>
          </section>

          {/* Audit Logs */}
          <section className="mb-16" id="audit">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Audit Logs</h2>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Track all significant actions within your organization for security and compliance.
              </p>
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">Logged Activities Include:</h4>
              <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex items-center space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span>User login/logout events</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span>Role and permission changes</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span>User invitation and removal</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span>Organization settings changes</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span>Data export requests</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span>Security policy updates</span>
                </div>
              </div>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/admin/permissions"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Permission System</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/admin/org-admin"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Organization Admin Guide</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default AdminSecurity;
